import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, MessageSquare, Pencil, Star } from 'lucide-react';
import { Project, Formation, Profile } from '../types';
import { localized, mediaUrl, yearOf, pitchOf, titleOf } from '../utils';
import { BACKEND_URL } from '../config';
import PageMeta from '../components/PageMeta';
import { HomeSkeleton } from '../components/Skeletons';
import SkeletonImage from '../components/SkeletonImage';
import ProjectRow from '../components/ProjectRow';
import ProjectLinks from '../components/ProjectLinks';
import { OPEN_CHAT } from '../components/ChatWidget';

interface HomeProps {
  authenticated: boolean;
}

const display = 'font-display font-extrabold tracking-[-0.04em]';
const pill = 'h-14 px-7 inline-flex items-center gap-2 rounded-full font-semibold transition-colors';

// Showcase uses its dedicated banner, falling back to the regular banner then the first screenshot.
function showcaseImage(p: Project): string | null {
  const base = `${BACKEND_URL}/projects/${p.strid}`;
  return mediaUrl(p.showcase_banner, base) ?? mediaUrl(p.banner, base) ?? mediaUrl(p.images[0], base);
}

export default function Home({ authenticated }: HomeProps) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [formations, setFormations] = useState<Formation[]>([]);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
    Promise.all([
      fetch(`${BACKEND_URL}/api/profile`).then(r => r.json()).catch(() => null),
      fetch(`${BACKEND_URL}/api/projects`, { headers }).then(r => r.json()).catch(() => []),
      fetch(`${BACKEND_URL}/api/formations`).then(r => r.json()).catch(() => []),
    ]).then(([profileData, projectsData, formationsData]) => {
      setProfile(profileData);
      setProjects(Array.isArray(projectsData) ? projectsData : []);
      setFormations(Array.isArray(formationsData) ? formationsData : []);
      setLoading(false);
    });
  }, []);

  if (loading) {
    return <HomeSkeleton />;
  }

  const featured = projects.filter(p => p.featured).sort((a, b) => (a.featured_order ?? 1e9) - (b.featured_order ?? 1e9));
  const recent = projects.slice(0, 6);
  const jobs = formations.filter(f => !f.is_study);
  const studies = formations.filter(f => f.is_study);
  const interests = profile ? (profile.interests[lang] || profile.interests['en'] || profile.interests['fr'] || []) : [];
  const firstName = profile?.name.split(' ')[0] ?? '';
  const [roleMain, roleRest] = (profile ? localized(profile.position, lang) : '').split(/ [-–] /);
  const description = profile ? localized(profile.description, lang) : '';
  const dates = (f: Formation) => {
    const start = yearOf(f.start_date);
    const end = f.ongoing ? t('today') : yearOf(f.end_date);
    return start === end ? start : `${start} — ${end}`;
  };

  return (
    <div>
      <PageMeta />

      {/* Hero */}
      <section className="pt-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 flex flex-wrap items-center gap-12">
          <div className="flex-[999_1_480px] min-w-0">
            <h1 className={`${display} text-5xl sm:text-7xl lg:text-[96px] leading-[0.95]`}>
              {profile?.tagline?.[lang]?.trim() ? profile.tagline[lang].replace('{name}', firstName) : t('hello', { name: firstName })}
            </h1>
            {roleMain && (
              <p className="mt-6 font-display text-2xl sm:text-[28px] leading-tight font-bold tracking-[-0.02em] text-ink max-w-2xl">
                {roleMain}{roleRest && <> <span className="text-primary-light">{roleRest}</span></>}
              </p>
            )}
            {description && <div className="mt-5 prose-content text-base sm:text-[17px] leading-relaxed max-w-xl" dangerouslySetInnerHTML={{ __html: description }} />}
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/cv" className={`${pill} bg-ink text-base hover:bg-primary hover:text-white`}>
                {t('see_my_cv')} <ArrowRight size={18} />
              </Link>
              {profile?.mail && (
                <a href={`mailto:${profile.mail}`} className={`${pill} border-[1.5px] border-ink text-ink hover:bg-surface`}>
                  {t('write_me')}
                </a>
              )}
              <button type="button" onClick={() => window.dispatchEvent(new Event(OPEN_CHAT))} className={`${pill} text-primary-light hover:bg-surface`}>
                <MessageSquare size={18} /> {t('ask_assistant')}
              </button>
              {authenticated && (
                <Link to="/admin?tab=profile" className={`${pill} text-zinc-500 hover:text-ink`}>
                  <Pencil size={16} /> {t('edit_profile')}
                </Link>
              )}
            </div>
          </div>
          {profile && (
            <div className="flex-[1_1_300px] max-w-[420px] mx-auto relative">
              <div className="aspect-[4/5] rounded-[28px] overflow-hidden bg-surface">
                <SkeletonImage src={profile.picture_url || `${BACKEND_URL}/profile-picture.png`} alt={profile.name} className="object-cover object-[70%_center]" />
              </div>
              {profile.openToWork !== false && (
                <p className="absolute left-3 sm:-left-6 top-8 -rotate-[4deg] rounded-full bg-primary text-white px-4 py-2.5 text-sm font-semibold shadow-lg">
                  {t('available_badge')}
                </p>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Showcase */}
      {featured.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex flex-col gap-7">
          {featured.map((p, i) => {
            const img = showcaseImage(p);
            const logo = mediaUrl(p.logo, `${BACKEND_URL}/projects/${p.strid}`);
            const dark = i % 2 === 0;
            return (
              <div
                key={p.strid}
                className={`group relative flex flex-wrap ${dark ? 'md:flex-row' : 'md:flex-row-reverse'} rounded-[28px] overflow-hidden ${dark ? 'bg-feature text-feature-fg' : 'bg-surface text-ink'}`}
              >
                <div className={`flex-[1_1_440px] min-h-[260px] md:min-h-[340px] relative ${dark ? 'bg-black/10' : 'bg-surface-2'}`}>
                  {img && <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />}
                </div>
                <div className="flex-[1_1_340px] p-8 sm:p-11 flex flex-col justify-between gap-7">
                  <div className={`flex flex-wrap justify-between gap-3 text-[13px] font-semibold ${dark ? 'text-feature-muted' : 'text-muted'}`}>
                    <span className="inline-flex items-center gap-1.5"><Star size={14} className="fill-current" /> {t('featured_label')}</span>
                    <span>{localized(p.type, lang)} · {yearOf(p.start_date)}</span>
                  </div>
                  <div>
                    {logo && <img src={logo} alt="" className="w-16 h-16 rounded-2xl object-cover mb-5" />}
                    <h2 className={`${display} text-4xl sm:text-5xl leading-none ${dark ? 'text-feature-fg' : 'text-ink'}`}>
                      {/* stretched link: whole card opens the project, link buttons sit above it */}
                      <Link to={`/projects/${p.strid}`} className="after:absolute after:inset-0">{titleOf(p, lang)}</Link>
                    </h2>
                    <p className={`mt-4 text-[17px] leading-relaxed ${dark ? 'text-feature-muted' : 'text-muted'}`}>{p.subtitle?.[lang]?.trim() || pitchOf(localized(p.description, lang))}</p>
                  </div>
                  <ProjectLinks project={p} className={`border ${dark ? 'border-white/35 hover:bg-white/15' : 'border-zinc-700 hover:bg-base'}`} />
                </div>
              </div>
            );
          })}
        </section>
      )}

      {/* Recent projects */}
      {recent.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
            <h2 className={`${display} text-4xl sm:text-[56px] leading-none`}>{t('recently')}</h2>
            <Link to="/projects" className="text-[15px] font-semibold text-primary-light border-b-2 border-primary-light pb-0.5">
              {t('all_projects', { count: projects.length })} →
            </Link>
          </div>
          <div className="border-t-[1.5px] border-ink">
            {recent.map(p => <ProjectRow key={p.strid} project={p} />)}
          </div>
        </section>
      )}

      {/* Career */}
      {formations.length > 0 && (
        <section className="bg-surface mt-8">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
            <h2 className={`${display} text-4xl sm:text-[56px] leading-none mb-10`}>{t('my_career')}</h2>
            <div className="grid md:grid-cols-2 gap-12">
              {[{ title: t('experiences_title'), list: jobs }, { title: t('formations'), list: studies }].map(col => (
                <div key={col.title}>
                  <h3 className="text-[13px] font-semibold uppercase tracking-[0.08em] text-muted mb-2">{col.title}</h3>
                  {col.list.map(f => (
                    <Link key={f.strid} to={`/formations#${f.strid}`} className="group flex justify-between gap-4 py-4 border-b border-zinc-700">
                      <span>
                        <span className="block text-[17px] font-semibold text-ink group-hover:text-primary-light transition-colors">
                          {(f.is_study ? localized(f.degree, lang) : localized(f.position, lang)) || f.name}
                        </span>
                        <span className="text-sm text-muted">{f.name}</span>
                      </span>
                      <span className="text-sm text-muted whitespace-nowrap">{dates(f)}</span>
                    </Link>
                  ))}
                </div>
              ))}
            </div>
            {interests.length > 0 && (
              <div className="mt-12">
                <h3 className="text-[13px] font-semibold uppercase tracking-[0.08em] text-muted mb-3">{t('outside_code')}</h3>
                <div className="flex flex-wrap gap-2">
                  {interests.map(i => <span key={i.name} className="px-3.5 py-2 rounded-full bg-base text-ink text-sm font-semibold">{i.name}</span>)}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Contact */}
      {profile && (
        <section className="bg-contact text-contact-fg">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 flex flex-wrap items-end justify-between gap-8">
            <h2 className={`${display} text-5xl sm:text-7xl lg:text-[80px] leading-[0.95] text-contact-fg`}>{t('lets_talk')}</h2>
            <div className="flex flex-wrap gap-2.5 text-[15px] font-semibold">
              {profile.mail && <a href={`mailto:${profile.mail}`} className="h-[52px] px-6 inline-flex items-center rounded-full bg-contact-fg text-contact">{profile.mail}</a>}
              {profile.linkedIn && <a href={profile.linkedIn} target="_blank" rel="noopener noreferrer" className="h-[52px] px-6 inline-flex items-center rounded-full border-[1.5px] border-current">LinkedIn</a>}
              {profile.github && <a href={profile.github} target="_blank" rel="noopener noreferrer" className="h-[52px] px-6 inline-flex items-center rounded-full border-[1.5px] border-current">GitHub</a>}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
